import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ReconnectBanner from "@/components/ReconnectBanner";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";
import AreaSavedChip from "@/components/AreaSavedChip";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Projects from "@/pages/Projects";
import ProjectWorkspace from "@/pages/ProjectWorkspace";
import WizardWorkspace from "@/pages/WizardWorkspace";
import CommandCentre from "@/pages/CommandCentre";
import GisMapSuite from "@/pages/GisMapSuite";
import RemoteSensingViewer from "@/pages/RemoteSensingViewer";
import AiGeoscientist from "@/pages/AiGeoscientist";
import GeologyStructure from "@/pages/GeologyStructure";
import Geochemistry from "@/pages/Geochemistry";
import Geophysics from "@/pages/Geophysics";
import Explorer3D from "@/pages/Explorer3D";
import SensorFusion from "@/pages/SensorFusion";
import TargetGeneration from "@/pages/TargetGeneration";
import QaQc from "@/pages/QaQc";
import InvestorSnapshot from "@/pages/InvestorSnapshot";
import Admin from "@/pages/Admin";
import SharePage from "@/pages/SharePage";
import Home from "@/pages/Home";
import MapGenWorkspace from "@/pages/MapGenWorkspace";
import GeophysWorkspace from "@/pages/GeophysWorkspace";
import GeoTeaserPage from "@/pages/GeoTeaserPage";
import GeoPortfolioPage from "@/pages/GeoPortfolioPage";
import ClientPortfolioPage from "@/pages/ClientPortfolioPage";
import CommodityExplorer from "@/pages/CommodityExplorer";
import PackSharePage from "@/pages/PackSharePage";
import Landing from "@/pages/Landing";
import { GeoPiProvider } from "@/context/GeoPiContext";
import GeoPiPanel from "@/components/GeoPiPanel";

function ModuleGuard({ moduleKey, children }) {
  const { user } = useAuth();
  if (user === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === null) return <Navigate to="/login" replace />;
  const ok = user.role === "super_admin" || (user.active_modules || []).includes(moduleKey);
  if (!ok) {
    toast.error("You do not have access to this module. Contact your administrator.");
    return <Navigate to="/console" replace />;
  }
  return children;
}

function Protected({ children, bare = false }) {
  const { user } = useAuth();
  if (user === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === null) return <Navigate to="/login" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function SuperAdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "super_admin") return <Navigate to="/console" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
        <GeoPiProvider>
        <ReconnectBanner />
        <GeoPiPanel />
        <PwaUpdateBanner />
        <AreaSavedChip />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/investor/:token" element={<InvestorSnapshot />} />
            <Route path="/geo-teaser/:token" element={<GeoTeaserPage />} />
            <Route path="/geo-portfolio/:token" element={<GeoPortfolioPage />} />
            <Route path="/client-portfolio/:token" element={<ClientPortfolioPage />} />
            <Route path="/pack/:token" element={<PackSharePage />} />
            <Route path="/" element={<Landing />} />
            <Route path="/console" element={<Protected bare><Home /></Protected>} />
            <Route path="/remote-sensing" element={<Protected><ModuleGuard moduleKey="remote-sensing"><Projects mode="remote-sensing" /></ModuleGuard></Protected>} />
            <Route path="/maps" element={<Protected><ModuleGuard moduleKey="maps"><Projects mode="maps" /></ModuleGuard></Protected>} />
            <Route path="/maps/:id" element={<Protected bare><ModuleGuard moduleKey="maps"><MapGenWorkspace /></ModuleGuard></Protected>} />
            <Route path="/geophysics" element={<Protected><ModuleGuard moduleKey="geophysics"><Projects mode="geophysics" /></ModuleGuard></Protected>} />
            <Route path="/geophysics/:id" element={<Protected bare><ModuleGuard moduleKey="geophysics"><GeophysWorkspace /></ModuleGuard></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:id" element={<Protected bare><WizardWorkspace /></Protected>} />
            <Route path="/projects/:id/command" element={<Protected bare><CommandCentre /></Protected>} />
            <Route path="/projects/:id/gis" element={<Protected bare><GisMapSuite /></Protected>} />
            <Route path="/projects/:id/eo" element={<Protected bare><RemoteSensingViewer /></Protected>} />
            <Route path="/projects/:id/ai" element={<Protected bare><AiGeoscientist /></Protected>} />
            <Route path="/projects/:id/geology" element={<Protected bare><GeologyStructure /></Protected>} />
            <Route path="/projects/:id/geochem" element={<Protected bare><Geochemistry /></Protected>} />
            <Route path="/projects/:id/geophysics" element={<Protected bare><Geophysics /></Protected>} />
            <Route path="/projects/:id/3d" element={<Protected bare><Explorer3D /></Protected>} />
            <Route path="/projects/:id/fusion" element={<Protected bare><SensorFusion /></Protected>} />
            <Route path="/projects/:id/targets" element={<Protected bare><TargetGeneration /></Protected>} />
            <Route path="/projects/:id/qaqc" element={<Protected bare><QaQc /></Protected>} />
            <Route path="/projects/:id/advanced" element={<Protected><ProjectWorkspace /></Protected>} />
            <Route path="/commodities" element={<Protected><CommodityExplorer /></Protected>} />
            <Route path="/admin" element={<Protected><SuperAdminOnly><Admin /></SuperAdminOnly></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </GeoPiProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
